import React, { useState } from 'react';
import DisplayText from 'components/ui-kits/DisplayText';
import { Checkbox } from 'components/ui-kits/Form/Checkbox';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from 'react-query';
import { submitMerchantDocument } from 'services/documentService';
import { handleError, handleSuccess } from 'utils/handleResponse';
import { MERCHANT_REGISTRATION_MESSAGE } from 'const/Message';
import { DocumentAccountResponse, DocumentId } from 'types/Document';
import {
  registerMerchantSubscriptionPlan,
  registerSubscriptionPlan
} from 'services/merchantService';
import Loader from 'components/Loader';
import { PAYMENT_TOKEN_KEY } from 'const/Const';
import { calculateTax, formatDollar, MONTHSINFRENCH } from 'utils';
import { Dialog } from 'components/Dialog';
import Stepper from 'components/Stepper';
import { COLORS } from 'styles/variables';
import { Merchant, MerchantSubscriptionPlan } from 'types/Merchant';
import { RootState } from 'app/store';
import { useSelector } from 'react-redux';

const now = new Date();
const monthShort = MONTHSINFRENCH[now.getMonth()];
const currentDate = now.getDate();

type TermsAndConditionsStandardPlanProps = {
  screenName: DocumentId['screen'];
  isClickNoThanks: boolean;
  hasSubscriptionPlan?: boolean;
  onClose: () => void;
  onAcceptSubmitted?: () => Promise<void>;
  onCustomAcceptNoThanks?: () => Promise<void>;
};

export default function TermsAndConditionsStandardPlan({
  screenName,
  isClickNoThanks,
  hasSubscriptionPlan,
  onClose,
  onAcceptSubmitted,
  onCustomAcceptNoThanks
}: TermsAndConditionsStandardPlanProps) {
  const queryClient = useQueryClient();
  const [isChangingSite, setIsChangingSite] = useState(false);
  const { merchantSlug } = useSelector((state: RootState) => state.user);
  const isSpecialMerchant =
    queryClient
      .getQueryData<Merchant>('getMerchant')
      ?.categories.some((category) => category.name.includes('Boulangeries')) ||
    false;

  const {
    mutateAsync: mutateMerchantRegisterSubscriptionPlan,
    isLoading: isLoadingRegisterSubscriptionPlan
  } = useMutation({
    mutationFn: registerMerchantSubscriptionPlan,
    onError: () => handleError(),
    onSettled: () => setIsChangingSite(false)
  });

  const { mutateAsync: mutateSubmitDocument, isLoading: isSubmittingDocument } =
    useMutation(
      ['submit-document', screenName],
      () => submitMerchantDocument(screenName),
      {
        onSuccess: async () => {
          handleSuccess(MERCHANT_REGISTRATION_MESSAGE.SUBMIT_DOCUMENT_SUCCESS);
          queryClient.invalidateQueries({
            queryKey: ['getMerchant']
          });
          await queryClient.invalidateQueries({
            queryKey: ['merchantDocuments', screenName]
          });

          if (hasSubscriptionPlan) {
            return;
          }

          await mutateMerchantRegisterSubscriptionPlan(window.origin).then(
            (data) => {
              if (
                data.data &&
                data.data.redirect_url &&
                data.data.payment_token
              ) {
                localStorage.setItem(
                  PAYMENT_TOKEN_KEY,
                  data.data.payment_token
                );

                setIsChangingSite(true);
                window.location.href = data.data.redirect_url;
              }
            }
          );
        },
        onError: () => {
          handleError(undefined, MERCHANT_REGISTRATION_MESSAGE.GENERAL_ERROR);
        }
      }
    );

  const { register, watch } = useForm({
    defaultValues: { acceptTerms: false }
  });
  const isAccepted = watch('acceptTerms');

  const linkToShow = isClickNoThanks
    ? 'https://achil.com/web/cgv-standard'
    : isSpecialMerchant
    ? 'https://achil.com/web/cgv-standard-boulangerie'
    : 'https://achil.com/web/cgv-standard-commercant';

  const {
    mutateAsync: mutateRegisterSubscriptionPlan,
    isLoading: isRegisterSubscriptionPlanLoading
  } = useMutation(
    ({ slug, data }: { slug: string; data: MerchantSubscriptionPlan }) =>
      registerSubscriptionPlan(slug, data),
    {
      onSuccess: async () => {
        queryClient.invalidateQueries({
          queryKey: ['getMerchant']
        });

        if (isClickNoThanks) {
          typeof onAcceptSubmitted === 'function' &&
            (await onAcceptSubmitted());
        } else {
          typeof onCustomAcceptNoThanks === 'function'
            ? await onCustomAcceptNoThanks()
            : await mutateSubmitDocument();
        }
      }
    }
  );

  const uploadedDocuments =
    queryClient.getQueryData<DocumentAccountResponse>([
      'merchantDocuments',
      screenName
    ])?.['hydra:member'] || [];

  const isShowTermsAndConditions =
    uploadedDocuments.some(({ state }) => state === 'new') &&
    hasSubscriptionPlan;

  const shouldShowLoader =
    isSubmittingDocument ||
    isChangingSite ||
    isLoadingRegisterSubscriptionPlan ||
    isRegisterSubscriptionPlanLoading;

  const onSubmit = async () => {
    await mutateRegisterSubscriptionPlan({
      slug: merchantSlug,
      data: {
        subscriptionMode: isYearly ? 'yearly' : 'monthly'
      }
    });
  };
  const isYearly = localStorage.getItem('isYearly') === 'true';
  const planPrice = Number(localStorage.getItem('planPrice'));

  const { taxAmount, beforeTax } = calculateTax(formatDollar(planPrice * 100));

  const step: React.ComponentPropsWithoutRef<typeof Stepper>['steps'] = [
    {
      id: 'first',
      header: 'Confirmez votre abonnement',
      headerButtonConfig: {
        closeButton: {
          hideCloseButton: true
        }
      },
      content: (
        <>
          <div
            style={{
              display: 'flex',
              alignItems: 'start',
              justifyContent: 'space-between',
              color: '#464A51',
              flexDirection: 'row'
            }}>
            <div>
              <DisplayText
                label="Formule STANDARD"
                fontWeight="BOLD"
                fontSize="L"
                margin="0 0 0.5rem 0"
              />
            </div>
            <div>
              <DisplayText
                fontSize="L"
                fontWeight="BOLD"
                textAlign="end"
                margin="0 0 0.5rem 0">
                {beforeTax}€ HT / {isYearly ? 'an' : 'mois'}
              </DisplayText>
              <DisplayText fontSize="M" textAlign="end">
                {isYearly
                  ? "60€ d'économie sans engagement"
                  : 'Sans engagement'}
              </DisplayText>
            </div>
          </div>
          <p
            style={{
              margin: '2.5rem 0 2.5rem 0',
              fontSize: '1.8rem',
              fontWeight: 'bold',
              textAlign: 'center',
              color: '#464A51'
            }}>
            Conditions générales de vente formule STANDARD
          </p>
          <DisplayText fontSize="M">
            Nous vous invitons à prendre connaissance des Conditions générales
            de vente du de la formule STANDARD en cliquant sur le lien
            ci-dessous :
          </DisplayText>
          <ul style={{ padding: '0 0 1.5rem 2rem' }}>
            <li
              style={{
                fontSize: '1.6rem',
                color: '#1976d2',
                margin: '2rem 0 0.5rem 2rem',
                textDecoration: 'underline'
              }}>
              <a
                href={linkToShow}
                rel="noreferrer"
                target="_blank"
                className="link">
                Consulter les Conditions générales de vente de la formule
                STANDARD
              </a>
            </li>
          </ul>
          <Checkbox
            fontSize="M"
            register={register}
            name="acceptTerms"
            id="acceptTerms">
            En cochant cette case, je confirme avoir lu et accepté les
            Conditions générales de vente de la formule STANDARD.
          </Checkbox>
        </>
      ),
      footerConfig: {
        previousButton: {
          label: 'Annuler',
          onClick: onClose
        },
        nextButton: { label: 'Confirmer', disabled: !isAccepted }
      }
    },
    {
      id: 'second',
      header: 'Confirmez votre abonnement',
      headerButtonConfig: {
        closeButton: {
          hideCloseButton: true
        }
      },
      content: (
        <>
          <div
            style={{
              display: 'flex',
              alignItems: 'start',
              justifyContent: 'space-between',
              color: '#464A51',
              flexDirection: 'row'
            }}>
            <DisplayText
              fontSize="L"
              fontWeight="BOLD"
              label="Formule STANDARD (HT)"
            />
            <div>
              <DisplayText
                fontSize="L"
                fontWeight="BOLD"
                textAlign="end"
                margin="0 0 0.5rem 0">
                {beforeTax}€
              </DisplayText>
            </div>
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'start',
              justifyContent: 'space-between',
              color: '#464A51',
              flexDirection: 'row',
              borderBottom: `1px solid ${COLORS.GREY_2}`,
              paddingBottom: '1rem',
              margin: '2rem 0 2rem 0'
            }}>
            <DisplayText
              fontSize="L"
              fontWeight="BOLD"
              margin="0 0 0.5rem 0"
              label="TVA (20%)"
            />
            <DisplayText
              fontSize="L"
              fontWeight="BOLD"
              margin="0 0 0.5rem 0"
              label={`${taxAmount}€`}
            />
          </div>

          <div
            style={{
              display: 'flex',
              alignItems: 'start',
              justifyContent: 'space-between',
              color: '#464A51',
              flexDirection: 'row'
            }}>
            <DisplayText
              fontSize="L"
              fontWeight="BOLD"
              margin="0 0 1.5rem 0"
              label="Total (TTC)"
            />
            <DisplayText
              fontSize="L"
              fontWeight="BOLD"
              margin="0 0 0.5rem 0"
              label={`${formatDollar(planPrice * 100)}€`}
            />
          </div>
          {isYearly ? (
            <DisplayText fontSize="M" margin="0 0 1rem 0">
              Ce montant sera débité chaque année le{' '}
              <span style={{ fontWeight: 'bold' }}>
                {currentDate} {monthShort}
              </span>{' '}
              sur votre carte bancaire.
            </DisplayText>
          ) : (
            <DisplayText fontSize="M" margin="0 0 1rem 0">
              Ce montant sera débité chaque mois le{' '}
              <span style={{ fontWeight: 'bold' }}>{currentDate} </span>
              sur votre carte bancaire.
            </DisplayText>
          )}
        </>
      ),
      footerConfig: {
        previousButton: {
          label: 'Annuler',
          onClick: onClose
        },
        nextButton: { label: 'Abonnez-vous', onClick: onSubmit }
      }
    }
  ];

  const termsAndConditions: React.ComponentPropsWithoutRef<
    typeof Stepper
  >['steps'] = [
    {
      id: 't&c',
      header: 'Conditions générales de vente formule STANDARD',
      headerButtonConfig: {
        closeButton: {
          hideCloseButton: true
        }
      },
      content: (
        <>
          <DisplayText fontSize="M">
            Nous vous invitons à prendre connaissance des Conditions générales
            de vente de notre formule STANDARD en cliquant sur le lien
            ci-dessous :
          </DisplayText>
          <ul style={{ padding: '0 0 1.5rem 2rem' }}>
            <li
              style={{
                fontSize: '1.6rem',
                color: '#1976d2',
                margin: '2rem 0 0.5rem 2rem',
                textDecoration: 'underline'
              }}>
              <a
                href={linkToShow}
                rel="noreferrer"
                target="_blank"
                className="link">
                Consulter les Conditions générales de vente de la formule
                STANDARD
              </a>
            </li>
          </ul>
          <Checkbox
            fontSize="M"
            register={register}
            name="acceptTerms"
            id="acceptTerms">
            En cochant cette case, je confirme avoir lu et accepté les
            Conditions générales de vente de la formule STANDARD.
          </Checkbox>
        </>
      ),
      footerConfig: {
        previousButton: {
          label: 'Annuler',
          onClick: onClose
        },
        nextButton: {
          label: 'Confirmer',
          disabled: !isAccepted,
          onClick: () => mutateSubmitDocument()
        }
      }
    }
  ];

  return (
    <>
      {shouldShowLoader ? <Loader /> : null}
      <Dialog maxWidth="70rem" minWidth="70rem">
        <Stepper steps={isShowTermsAndConditions ? termsAndConditions : step} />
      </Dialog>
    </>
  );
}
